'use client';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ArrowRight, Dna, Play } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import GuestDreamModal from '@/components/GuestDreamModal';

const FALLBACK_ROTATING_WORDS = [
  'Businesses',
  'Books',
  "Children\'s Books",
  'Comics',
  'Courses',
  'Brands',
  'Products',
  'Apps',
  'Websites',
  'Marketing Campaigns',
  'AI Businesses',
  'Dreams',
];

const FALLBACK_SAMPLE_PROMPTS = [
  "I want to publish a children\'s book.",
  'I want to build a tattoo studio.',
  'I want to start an AI consulting company.',
  'I want passive income.',
  'I want to create a language school.',
  'I want to sell handmade products.',
  'I want to launch a mobile app.',
];

const FALLBACK_JOURNEY_STEPS = [
  { emoji: '💡', label: 'Dream' },
  { emoji: '🧬', label: 'DreamDNA™' },
  { emoji: '🎨', label: 'Brand' },
  { emoji: '📦', label: 'Products' },
  { emoji: '🌐', label: 'Website' },
  { emoji: '📣', label: 'Marketing' },
  { emoji: '🚀', label: 'Launch' },
  { emoji: '📈', label: 'Scale' },
  { emoji: '⚙️', label: 'Automate' },
  { emoji: '🕊️', label: 'Freedom' },
];

export default function HeroSection() {
  const router = useRouter();
  const { t } = useLanguage();
  const { user } = useAuth();
  const db = t?.dreamBuilder;

  const rotatingWords = useMemo(
    () => (db?.rotatingWords?.length ? db.rotatingWords : FALLBACK_ROTATING_WORDS),
    [db?.rotatingWords],
  );
  const samplePrompts = useMemo(
    () => (db?.heroSamplePrompts?.length ? db.heroSamplePrompts : FALLBACK_SAMPLE_PROMPTS),
    [db?.heroSamplePrompts],
  );
  const journeySteps = useMemo(
    () => (db?.journeySteps?.length ? db.journeySteps : FALLBACK_JOURNEY_STEPS),
    [db?.journeySteps],
  );

  const [showGuestModal, setShowGuestModal] = useState(false);
  const [pendingDream, setPendingDream] = useState('');

  const [wordIndex, setWordIndex] = useState(0);
  const [wordVisible, setWordVisible] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const isPausedRef = useRef(false);
  const wordTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    setWordIndex(0);
  }, [rotatingWords]);

  useEffect(() => {
    wordTimerRef.current = setInterval(() => {
      if (isPausedRef.current) return;
      setWordVisible(false);
      setTimeout(() => {
        setWordIndex((prev) => (prev + 1) % rotatingWords.length);
        setWordVisible(true);
      }, 350);
    }, 2500);
    return () => {
      if (wordTimerRef.current) clearInterval(wordTimerRef.current);
    };
  }, [rotatingWords.length]);

  const [promptIndex, setPromptIndex] = useState(0);
  const [promptVisible, setPromptVisible] = useState(true);

  useEffect(() => {
    setPromptIndex(0);
  }, [samplePrompts]);

  useEffect(() => {
    const timer = setInterval(() => {
      setPromptVisible(false);
      setTimeout(() => {
        setPromptIndex((prev) => (prev + 1) % samplePrompts.length);
        setPromptVisible(true);
      }, 400);
    }, 3200);
    return () => clearInterval(timer);
  }, [samplePrompts.length]);

  const [dream, setDream] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const handleStart = useCallback(() => {
    if (!dream.trim()) return;
    if (!user) {
      setPendingDream(dream.trim());
      setShowGuestModal(true);
      return;
    }
    const encoded = encodeURIComponent(dream.trim());
    router.push(`/dream-workspace?dream=${encoded}`);
  }, [dream, user, router]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleStart();
    }
  };

  const particles = useMemo(
    () => [
      { w: 3, h: 3, top: '12%', left: '8%', opacity: 0.18, color: '#a78bfa', dur: '8s', delay: '0s' },
      { w: 2, h: 2, top: '25%', left: '92%', opacity: 0.14, color: '#fb923c', dur: '11s', delay: '1.5s' },
      { w: 4, h: 4, top: '65%', left: '5%', opacity: 0.12, color: '#34d399', dur: '9s', delay: '3s' },
      { w: 2, h: 2, top: '80%', left: '88%', opacity: 0.16, color: '#a78bfa', dur: '13s', delay: '0.8s' },
      { w: 3, h: 3, top: '45%', left: '96%', opacity: 0.1, color: '#fb923c', dur: '10s', delay: '2.2s' },
      { w: 2, h: 2, top: '90%', left: '20%', opacity: 0.13, color: '#34d399', dur: '12s', delay: '4s' },
      { w: 3, h: 3, top: '35%', left: '2%', opacity: 0.11, color: '#a78bfa', dur: '7s', delay: '1s' },
      { w: 2, h: 2, top: '55%', left: '78%', opacity: 0.15, color: '#fb923c', dur: '14s', delay: '5s' },
      { w: 4, h: 4, top: '18%', left: '55%', opacity: 0.09, color: '#34d399', dur: '16s', delay: '2s' },
      { w: 2, h: 2, top: '72%', left: '42%', opacity: 0.12, color: '#a78bfa', dur: '9.5s', delay: '3.5s' },
    ],
    [],
  );

  const charLabel =
    dream.length > 0
      ? (db?.charCount ?? '{count} chars').replace('{count}', String(dream.length))
      : (db?.inputHint ?? 'Press Enter to analyze');

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden gradient-hero card-grid-bg pt-16">
      <div className="absolute top-1/4 left-1/4 w-96 h-96 blob-primary pointer-events-none opacity-60" />
      <div className="absolute bottom-1/3 right-1/4 w-80 h-80 blob-accent pointer-events-none opacity-50" />
      <div className="absolute top-1/2 right-1/3 w-64 h-64 blob-mint pointer-events-none opacity-40" />

      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        {particles.map((p, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              width: p.w,
              height: p.h,
              top: p.top,
              left: p.left,
              opacity: p.opacity,
              background: p.color,
              animation: `heroParticleFloat ${p.dur} ease-in-out ${p.delay} infinite alternate`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center w-full">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card border border-primary/20 mb-8">
          <Dna size={14} className="text-primary" />
          <span className="text-xs font-600 text-primary">{db?.badge ?? 'Dream DNA Studio™ Platform'}</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-mint/15 text-mint font-600">
            {t?.homePage?.heroNew ?? 'New'}
          </span>
        </div>

        <h1 className="text-5xl md:text-6xl lg:text-7xl font-800 text-foreground mb-3 leading-[1.08] tracking-tight">
          {db?.buildAnythingHeadline ?? 'Build Anything.'}
        </h1>

        <div
          className="flex items-center justify-center gap-3 mb-5 cursor-default select-none"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <span className="text-xl md:text-2xl font-600 text-muted-foreground">
            {db?.includingLabel ?? 'Including'}
          </span>
          <span
            className="text-xl md:text-2xl font-800 gradient-text"
            style={{
              opacity: wordVisible ? 1 : 0,
              transform: wordVisible ? 'translateY(0px)' : 'translateY(6px)',
              transition: 'opacity 0.35s ease, transform 0.35s ease',
              minWidth: '180px',
              display: 'inline-block',
              textAlign: 'left',
            }}
          >
            {rotatingWords[wordIndex]}
          </span>
        </div>

        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-4 leading-relaxed">
          {db?.subheadline ??
            'AIMakerStudio analyzes the full DNA of your dream — then builds your brand, products, content, launch plan, and progress tracking.'}
        </p>

        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/8 border border-primary/20 mb-8">
          <Dna size={13} className="text-primary" />
          <span className="text-xs text-primary font-600">
            {db?.dnaMethodologyLabel ??
              'Powered by Dream DNA Studio™ — our proprietary dream-to-reality methodology'}
          </span>
        </div>

        <div className="relative max-w-2xl mx-auto mb-4">
          <div
            className="glass-card rounded-2xl p-1 shadow-2xl transition-all duration-300"
            style={{
              borderColor: isFocused ? 'rgba(167, 139, 250, 0.6)' : 'rgba(167, 139, 250, 0.3)',
              borderWidth: '1px',
              borderStyle: 'solid',
              boxShadow: isFocused
                ? '0 0 0 3px rgba(167, 139, 250, 0.12), 0 20px 60px rgba(167, 139, 250, 0.18)'
                : '0 20px 60px rgba(167, 139, 250, 0.08)',
            }}
          >
            <textarea
              id="dream-input"
              data-tour="dream-input"
              value={dream}
              onChange={(e) => setDream(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              rows={3}
              className="w-full bg-transparent px-5 pt-4 pb-2 text-base text-foreground resize-none outline-none placeholder-muted-foreground/50 transition-all duration-300"
              placeholder={
                db?.heroInputPlaceholder ??
                'Tell DreamDNA™ the dream that keeps you awake at night...'
              }
              style={{ caretColor: 'var(--primary)' }}
            />
            <div className="flex items-center justify-between px-4 pb-3 pt-1">
              <span className="text-xs text-muted-foreground/50">{charLabel}</span>
              <button
                data-tour="start-button"
                onClick={handleStart}
                disabled={!dream.trim()}
                className="flex items-center gap-2 btn-primary px-6 py-2.5 rounded-xl text-sm font-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 hover:scale-105 active:scale-95"
              >
                <Dna size={15} />
                {db?.startBtn ?? 'Discover My Dream DNA™'}
                <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>

        <div className="mb-12 h-6 flex items-center justify-center gap-2">
          <span className="text-xs text-muted-foreground/50">{db?.tryLabel ?? 'Try:'}</span>
          <button
            className="text-xs text-primary/70 italic hover:text-primary transition-colors duration-200"
            style={{
              opacity: promptVisible ? 1 : 0,
              transform: promptVisible ? 'translateY(0px)' : 'translateY(4px)',
              transition: 'opacity 0.4s ease, transform 0.4s ease',
              display: 'inline-block',
            }}
            onClick={() => setDream(samplePrompts[promptIndex])}
          >
            &ldquo;{samplePrompts[promptIndex]}&rdquo;
          </button>
        </div>

        {/* Demo CTA — visible to non-signed-in users */}
        {!user && (
          <div className="flex items-center justify-center gap-3 mb-8">
            <Link
              href="/demo"
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-600 border border-border text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all"
            >
              <Play size={13} />
              {db?.tryDemoBtn ?? 'Try Interactive Demo'}
            </Link>
          </div>
        )}

        <div className="relative">
          <div
            className="absolute top-[22px] left-[5%] right-[5%] h-px hidden md:block overflow-hidden pointer-events-none"
            aria-hidden="true"
          >
            <div
              className="h-full w-full"
              style={{
                background:
                  'linear-gradient(90deg, transparent 0%, rgba(167,139,250,0.5) 20%, rgba(251,146,60,0.5) 50%, rgba(52,211,153,0.5) 80%, transparent 100%)',
                backgroundSize: '200% 100%',
                animation: 'heroFlowingLine 4s linear infinite',
              }}
            />
          </div>

          <div className="flex flex-wrap items-start justify-center gap-y-4 gap-x-2 md:gap-x-3">
            {journeySteps.map((step, i) => (
              <div key={`${step.label}-${i}`} className="flex flex-col items-center gap-1.5 relative z-10">
                <div
                  className="w-11 h-11 rounded-2xl glass-card border border-border flex items-center justify-center text-xl transition-all duration-300 hover:scale-110 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/15 cursor-default"
                  style={{
                    animation: `heroParticleFloat ${3.5 + i * 0.3}s ease-in-out ${i * 0.15}s infinite alternate`,
                  }}
                >
                  {step.emoji}
                </div>
                <span className="text-[10px] font-600 text-muted-foreground whitespace-nowrap">
                  {step.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showGuestModal && (
        <GuestDreamModal dream={pendingDream} onClose={() => setShowGuestModal(false)} />
      )}

      <style jsx>{`
        @keyframes heroParticleFloat {
          0% {
            transform: translateY(0px);
          }
          100% {
            transform: translateY(-10px);
          }
        }
        @keyframes heroFlowingLine {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </section>
  );
}
